import { QMulti } from '@educraft/base';
import { IExercise, ExerciseData, addResult } from '../exercise';
import { PdfUnit, PdfRect, PdfBrush, ETextAlign } from '../../pdf/pdf';
import { GUtil } from '../../pdf/gutil';
import { makeCombinedMulti, generateUnique } from './exeUtil';

export class ExerciseAbc implements IExercise {
  readonly name = 'ExerciseAbc';
  readonly fixedHeight = 0;
  readonly noWindow = false;
  readonly injectEq = false;
  readonly injectAbc = true;

  private rowHeight = PdfUnit.FromMillimeters(6);
  private squareSize = PdfUnit.FromMillimeters(5);
  private squareMargin = PdfUnit.FromMillimeters(1);
  private marginColumn = PdfUnit.FromMillimeters(3);
  private marginRow = PdfUnit.FromMillimeters(2);

  draw(data: ExerciseData): void {
    const selAbc = data.item.selAbc.length > 0 ? data.item.selAbc : data.item.selEq;
    if (selAbc.length === 0) return;

    const gen = makeCombinedMulti(data);

    // Sample 20 items to measure max cell dimensions
    const samples: QMulti[] = [];
    let maxCellW = 0;
    let maxCellH = 0;

    for (let i = 0; i < 20; i++) {
      const q = gen.generate(data.data);
      samples.push(q);

      const { cellW, cellH } = this.measureItem(data, q);
      maxCellW = Math.max(maxCellW, cellW);
      maxCellH = Math.max(maxCellH, cellH);
    }

    // Calculate grid dimensions
    const countX = Math.max(1, Math.floor(data.rect.width / (maxCellW + this.marginColumn)));
    const countY = Math.max(1, Math.floor(data.rect.height / (maxCellH + this.marginRow)));
    const total = countX * countY;

    const items = generateUnique(
      gen,
      data.data,
      total,
      (q) => `${q.texts.join(' ')}|${q.answers[q.answerIndex]}`,
      samples
    );

    const gridCellW = (data.rect.width + this.marginColumn) / countX;
    const gridCellH = (data.rect.height + this.marginRow) / countY;

    for (let x = 0; x < countX; x++) {
      for (let y = 0; y < countY; y++) {
        const index = y + x * countY;
        if (index >= items.length) break;
        const item = items[index];

        addResult(data, item.texts.join(' '), String.fromCharCode(item.answerIndex + 65));

        const cellRect = new PdfRect(
          data.rect.x + x * gridCellW,
          data.rect.y + y * gridCellH,
          gridCellW - this.marginColumn,
          gridCellH - this.marginRow
        );

        this.drawItem(data, item, cellRect);
      }
    }
  }

  /** Measure the width and height a single QMulti item needs. */
  private measureItem(data: ExerciseData, q: QMulti): { cellW: number; cellH: number } {
    // Width of question text
    let textW = 0;
    for (const t of q.texts) {
      const sz = data.page.measureString(t, GUtil.exerciseFont);
      textW = Math.max(textW, sz.width);
    }

    // Width of all answer options laid out side by side: each is [square][margin][answerText][gap]
    let answersW = 0;
    for (const a of q.answers) {
      const sz = data.page.measureString(a, GUtil.exerciseFont);
      answersW += this.squareSize + this.squareMargin + sz.width + this.marginColumn;
    }

    const cellW = Math.max(textW, answersW);
    const cellH = this.rowHeight * q.texts.length + this.rowHeight; // text lines + 1 answer row

    return { cellW, cellH };
  }

  /** Draw a single QMulti item inside the given cell rect. */
  private drawItem(data: ExerciseData, item: QMulti, rect: PdfRect): void {
    let top = rect.y;

    // Draw question text lines
    for (let i = 0; i < item.texts.length; i++) {
      data.page.drawString(
        item.texts[i],
        GUtil.exerciseFont,
        PdfBrush.Black,
        null,
        new PdfRect(rect.x, top, rect.width, this.rowHeight),
        ETextAlign.CenterLeft
      );
      top += this.rowHeight;
    }

    // Draw answer options in a row, evenly spaced
    const answerSlotW = rect.width / item.answers.length;

    for (let i = 0; i < item.answers.length; i++) {
      const slotX = rect.x + i * answerSlotW;

      // Checkbox square
      const r1 = new PdfRect(
        slotX,
        top + (this.rowHeight - this.squareSize) / 2,
        this.squareSize,
        this.squareSize
      );
      data.colorSchema.drawResult(data.page, r1);
      data.page.drawRectangle(GUtil.basePen, null, r1);

      // Answer text
      const r2 = new PdfRect(
        r1.right + this.squareMargin,
        top,
        answerSlotW - this.squareSize - this.squareMargin,
        this.rowHeight
      );
      data.page.drawString(
        item.answers[i],
        GUtil.exerciseFont,
        PdfBrush.Black,
        null,
        r2,
        ETextAlign.CenterLeft
      );
    }
  }
}
